import { fetchAuthSession, deleteUser, signOut, getCurrentUser } from "aws-amplify/auth";
import { EventSystem, MessageType, ToastMessage, ToastMessageEvent } from "./EventSystem";
import { Amplify } from "aws-amplify";
import amplifyconfig from "../amplifyconfiguration.json";
import { Hub } from "aws-amplify/utils";
import { DataStore, AuthModeStrategyType, syncExpression } from "aws-amplify/datastore";

import { disableWakeLock, enableWakeLock } from "./WakeLock";
import { DEFAULT_USER_INFO, IUserInfo } from "./backend/cloud/UserTable";
import { JobQueueInterface } from "./JobQueueInterface";
import { TileDownloaderBrowser } from "./TileDownloaderBrowser";
import { TileCache } from "./TileCache";
import { ILocation } from "./models/ILocation";
import { MediaLibrary } from "./MediaLibrary";
import { CachedUserInfo, User } from "./User";
import { LocalStorageProperty, resetAllLocalStorageProperties } from "./LocalStorageProperty";
import { Journal, Place } from "../models";

import { CHECK_FOR_UPDATE_MINUTES, JOURNAL_ENTRIES_NUM_DAYS } from "./Constants";
import { GeoLocation as GeoLocation } from "./GeoLocation";
import { NavigationService } from "./NavigatorService";

import * as Version from "../version.json";
import { initOrientation } from "./Orientation";
import { ModuleLog, getModuleLog, setLogLevel } from "./Logger";
import { IPlaceFilter, makeDefaultPlaceFilter } from "./backend/cloud/PlaceFilterTable";
import { LocalDataStore } from "./DataStore/LocalDataStore";
import { createDataStore } from "./DataStore/InitStore";
import { createGraphQLDataStore } from "./DataStore/InitGraphQLStore";
import { PlaceMediaDownloader } from "./PlaceMediaDownloaderBrowser";
import { UserSettings } from "./backend/UserSettings";
import { DataProvider } from "./DataStore/DataProvider";
import { UserTable } from "./backend/graphql/UserTable";
import { cloneDeep } from "lodash-es";
import { DateTime } from "luxon";

export const MAP_BOUNDS_CHANGED_EVENT = "map-bounds-changed";
export const SHOW_COMPASS_CHANGED_EVENT = "show-compass-changed";

export function showFooterMessage(msg: string, type: MessageType, duration = -1) {
    Kernel.instance().events.raiseEvent(ToastMessage, {
        msg: `${msg}`,
        duration: duration,
        type: type,
    } as ToastMessageEvent);
}

export type MapBounds = {
    sw: ILocation;
    ne: ILocation;
};

export enum DatabaseBackend {
    DataStore = "datastore",
    GraphQL = "graphql",
}

// Provides singleton access to core services.
export class Kernel {
    private static _instance: Kernel | undefined;

    static instance() {
        if (Kernel._instance === undefined) {
            Kernel._instance = new Kernel();
        }

        return Kernel._instance;
    }

    public readonly events: EventSystem = new EventSystem();

    private _log: ModuleLog;

    private _user: User | undefined;
    get user() {
        return this._user;
    }

    private _tileDownloader: JobQueueInterface | undefined;
    get tileDownloader() {
        return this._tileDownloader as JobQueueInterface;
    }

    private _placeMediaDownloader: JobQueueInterface | undefined;
    get placeMediaDownloader() {
        return this._placeMediaDownloader as JobQueueInterface;
    }

    private _tileCache: TileCache | undefined;
    get tileCache() {
        return this._tileCache as TileCache;
    }

    private _mediaLibrary: MediaLibrary | undefined;
    get mediaLibrary() {
        return this._mediaLibrary as MediaLibrary;
    }

    private _geoLocation: GeoLocation | undefined;
    get geoLocation() {
        return this._geoLocation as GeoLocation;
    }

    private _dataStore: LocalDataStore | undefined;
    get dataStore() {
        return this._dataStore as LocalDataStore;
    }

    private _userSettings: UserSettings | undefined;
    get userSettings() {
        return this._userSettings as UserSettings;
    }

    private _navigator: NavigationService | undefined;

    /**
     * Browser persistent settings
     */
    private _mapBounds = new LocalStorageProperty(
        this.events,
        "map-bounds",
        1,
        MAP_BOUNDS_CHANGED_EVENT
    );
    public showCompass = new LocalStorageProperty(
        this.events,
        "show-compass",
        1,
        SHOW_COMPASS_CHANGED_EVENT,
        false
    );

    public zoomOnFilterChange = new LocalStorageProperty(
        this.events,
        "zoom-on-filter-change",
        1,
        "zoom-on-filter-change",
        true
    );

    public featureHeaderFilterBar = new LocalStorageProperty(
        this.events,
        "feature-header-filter-bar",
        1,
        undefined,
        false
    );

    public currentPlaceFilter = new LocalStorageProperty(
        this.events,
        "current-place-filter",
        9,
        undefined,
        makeDefaultPlaceFilter()
    );

    public enableTileCache = new LocalStorageProperty(
        this.events,
        "enable-tile-cache",
        3,
        undefined,
        false
    );

    public enableDebugInfo = new LocalStorageProperty(
        this.events,
        "enable-debug-info",
        1,
        undefined,
        false
    );

    public loggingLevel = new LocalStorageProperty(this.events, "log-level", 1, undefined, "info");
    public amplifyLoggingLevel = new LocalStorageProperty(
        this.events,
        "amplify-log-level",
        1,
        undefined,
        "ERROR"
    );

    public currentUserInfo = new LocalStorageProperty(
        this.events,
        "current-user",
        1,
        "current-user",
        undefined
    );

    public currentUserSettings = new LocalStorageProperty(
        this.events,
        "current-user-settings",
        1,
        "current-user-settings",
        DEFAULT_USER_INFO
    );

    public currentHomeLocation = new LocalStorageProperty(
        this.events,
        "home-location",
        1,
        "home-location",
        undefined
    );

    public mapTileLayer = new LocalStorageProperty(
        this.events,
        "map-tile-layer",
        3,
        "map-tile-layer",
        "street"
    );

    public journalEnabled = new LocalStorageProperty(
        this.events,
        "journal-enabled",
        3,
        "journal-enabled",
        true
    );

    private _trackingLocation = false;
    public readonly currentVersion: string = Version.buildVersion;
    public readonly backend: DatabaseBackend;
    public readonly collection: string;
    public readonly siteRootUrl: string;

    constructor() {
        Kernel._instance = this;
        this.backend = (window as any).cavedbBackend ?? DatabaseBackend.DataStore;
        this.collection = (window as any).cavedbCollection ?? "c";
        this.siteRootUrl = (window as any).cavedbRootUrl;

        (window as any).LOG_LEVEL = this.amplifyLoggingLevel.get();

        setLogLevel(this.loggingLevel.get());
        this._log = getModuleLog("kernel");
        this._log.debug(`Collection: ${this.collection}`);
        this._log.debug(`Backend: ${this.backend}`);
        this._log.debug("Initialized cloud");
        this._init();
    }

    public saveUserSettings(settings: IUserInfo) {
        this.currentUserSettings.set(settings);
    }

    public isInCollection(collections: string | undefined | null) {
        if (!collections) {
            return true;
        }
        return collections.includes(this.collection);
    }

    private _init() {
        this._mediaLibrary = new MediaLibrary(this);
        initOrientation(this.events);

        //TODO: remove when we figure out how to make Amplify generate aws-exports.js with these already in
        amplifyconfig.aws_appsync_graphqlEndpoint = "https://api.cavedb.net/graphql";

        if (this.backend === DatabaseBackend.DataStore) {
            Amplify.configure(amplifyconfig);

            this._setSyncExpressions();

            DataStore.configure({
                authModeStrategyType: AuthModeStrategyType.MULTI_AUTH,
                maxRecordsToSync: 30000,
                syncPageSize: 10000,
            });

            this._registerAuthEvents();
            this._registerDataStoreEvents();

            this.getUser();

            this._dataStore = createDataStore();
        } else if (this.backend === DatabaseBackend.GraphQL) {
            Amplify.configure(amplifyconfig);

            Amplify.configure({
                API: {
                    GraphQL: {
                        defaultAuthMode: "apiKey",
                        apiKey: amplifyconfig.aws_appsync_apiKey,
                        endpoint: amplifyconfig.aws_appsync_graphqlEndpoint,
                        region: amplifyconfig.aws_appsync_region,
                    },
                },
            });
            this._registerAuthEvents();
            this._dataStore = createGraphQLDataStore();
        }

        this._getLatestUserInfo();

        this._tileCache = new TileCache();
        this._tileDownloader = new TileDownloaderBrowser(this, this._tileCache);
        this._placeMediaDownloader = new PlaceMediaDownloader(this);
        this._geoLocation = new GeoLocation(this.events);

        this._tileDownloader.init();
        this._placeMediaDownloader.init();
        this._mediaLibrary.init();
        this._userSettings = new UserSettings(
            this._dataStore?.getSource("users") as DataProvider,
            this._mediaLibrary
        );

        this.events.subscribe("filter-changed", (filter: IPlaceFilter) => {
            this.currentPlaceFilter.set(filter);
        });

        setInterval(
            async () => {
                await this.checkForNewVersion();
            },
            CHECK_FOR_UPDATE_MINUTES * 60 * 1000
        );

        this._log.debug("Kernel init complete");
    }

    private _setSyncExpressions() {
        const journalsSince = DateTime.utc().minus({ days: JOURNAL_ENTRIES_NUM_DAYS }).toISODate();

        const includeCave = this.collection === "c";
        const includeScuba = this.collection === "s";

        // Limit the number of journal entries synced to those added recently
        let journalExpr = syncExpression(Journal, (journal) => {
            return journal?.id.eq("0");
        });
        if (this.journalEnabled.get()) {
            if ((includeCave && includeScuba) || (!includeCave && !includeScuba)) {
                journalExpr = syncExpression(Journal, (journal) =>
                    journal?.addedAt.gt(journalsSince)
                );
            } else if (includeCave) {
                journalExpr = syncExpression(Journal, (journal) =>
                    journal?.and((journal) => [
                        journal?.addedAt.gt(journalsSince),
                        journal?.caveCollection.eq("true"),
                    ])
                );
            } else if (includeScuba) {
                journalExpr = syncExpression(Journal, (journal) =>
                    journal?.and((journal) => [
                        journal?.addedAt.gt(journalsSince),
                        journal?.scubaCollection.eq("true"),
                    ])
                );
            }
        }

        let placeExpression: any;
        if ((includeCave && includeScuba) || (!includeCave && !includeScuba)) {
            placeExpression = syncExpression(Place, (place) => place?.softDeleted.eq("false"));
        } else if (includeCave) {
            placeExpression = syncExpression(Place, (place) =>
                place?.and((place) => [
                    place?.softDeleted.eq("false"),
                    place?.caveCollection.eq("true"),
                ])
            );
        } else if (includeScuba) {
            placeExpression = syncExpression(Place, (place) =>
                place?.and((place) => [
                    place?.softDeleted.eq("false"),
                    place?.scubaCollection.eq("true"),
                ])
            );
        }
        DataStore.configure({
            syncExpressions: [journalExpr, placeExpression],
        });
    }

    private async _getLatestUserInfo() {
        // if we are offline then we can't do this but they must have logged on while online so
        // we will have already created the user record and cached it in browser storage.
        if (navigator.onLine) {
            try {
                // when we are using the DataStore we would wait until the whole store is synchronized
                // before we could get the latest user info. We don't want to do that so we just use the
                // graphQL interface instead
                const user = await getCurrentUser();
                if (user) {
                    const store = new LocalDataStore();
                    const userTable = new UserTable({ name: "users-gql", store });
                    const settings = new UserSettings(userTable, this.mediaLibrary);
                    let info = await settings.read(user.userId);
                    if (!info) {
                        // no user info so create one
                        const newItem = cloneDeep(DEFAULT_USER_INFO);
                        info = await userTable.create(newItem);
                    }
                    if (info) {
                        this.currentUserSettings.set(info);
                    }
                }
            } catch (e: any) {
                //no-op
                //TODO: Can get here if it failed to refresh the user tokens so we need to sign the user out
                this._log.info(e.toString());
                //await this._signOut();
            }
        }
    }

    public preventScreenTurningOff() {
        enableWakeLock();
    }

    public allowScreenTurningOff() {
        disableWakeLock();
    }

    public setMapBounds(sw: ILocation, ne: ILocation) {
        const bounds = {
            sw,
            ne,
        } as MapBounds;

        this._mapBounds.set(bounds);
        this.events.raiseEvent(MAP_BOUNDS_CHANGED_EVENT, bounds);
    }

    public getMapBounds(): MapBounds | undefined {
        const lastBounds = this._mapBounds.get();
        if (lastBounds) {
            if ("sw" in lastBounds && "ne" in lastBounds) {
                return lastBounds as MapBounds;
            }
        }
        return undefined;
    }

    public trackLocation(on: boolean) {
        if (on) {
            if (!this._trackingLocation) {
                this.geoLocation.start();
            }
        } else {
            if (this._trackingLocation) {
                this.geoLocation.stop();
            }
        }
        this._trackingLocation = on;
    }

    public startNavigating(location: ILocation) {
        if (this._navigator) {
            this.stopNavigating();
        }
        this._navigator = new NavigationService(
            this.events,
            this.geoLocation,
            this.currentUserSettings.get().navigationAccuracy
        );
        this._navigator.start(location);
        this.trackLocation(true);
        this.preventScreenTurningOff();
    }

    public stopNavigating() {
        if (this._navigator) {
            this._navigator.stop();
            this._navigator = undefined;
        }

        this.trackLocation(false);
        this.allowScreenTurningOff();
    }

    public async checkForNewVersion() {
        try {
            const response = await window.fetch("./dist/version.json");
            if (response.status === 200) {
                const newVersion = await response.json();
                if (this.currentVersion !== newVersion.buildVersion) {
                    this.events.raiseEvent("ds-new-version-available");
                }
            } else {
                this._log.warn("Failed to check for new version: {0} ", {
                    status: response.status,
                    msg: response.statusText,
                });
            }
        } catch (ex) {
            // suck up failures
            this._log.warn(`Failed checking for new version: ${ex}`);
        }
    }

    public hasUser(): boolean {
        return this._user !== undefined;
    }

    public getUser() {
        if (this._user) {
            return this._user;
        }
        try {
            const curUser = this.currentUserInfo.get();

            if (curUser) {
                this._user = new User(curUser);
                return this._user;
            }
        } catch (_ex) {
            this._log.warn(`Failed to get authenticated user - ${_ex}`);
        }
        return undefined;
    }

    public async signOut() {
        // stop syncronising as signed out will be delayed until that completes
        await DataStore.stop();

        await signOut();
        await this._clearDataStore();
    }

    public async deleteAccount() {
        await deleteUser();
        await this._clearDataStore();
        location.reload();
    }

    private async _clearDataStore() {
        await DataStore.clear();
    }

    private _createAWSHubHandler(
        channel: string,
        displayText: string,
        dsEventName: string,
        data: any = undefined,
        toast = true,
        duration = -1
    ) {
        // raise generic auth event so lazy component writers can respond to
        // anything
        this.events.raiseEvent(channel, {
            event: dsEventName,
            payload: data,
        });
        this.events.raiseEvent(dsEventName, data);
        if (toast) {
            showFooterMessage(displayText, MessageType.Info, duration);
        }
    }

    private async _setCurrentUserInfo(): Promise<CachedUserInfo | undefined> {
        const { tokens } = await fetchAuthSession();
        if (tokens && tokens.idToken && tokens.accessToken) {
            const ui = {
                groups: tokens.accessToken.payload["cognito:groups"],
                id: tokens.idToken.payload.sub,
                email: tokens.idToken.payload.email,
            } as CachedUserInfo;
            this.currentUserInfo.set(ui);
            return ui;
        } else {
            this.currentUserInfo.reset();
        }
        return undefined;
    }

    private async _signOut() {
        await this._clearDataStore();
        this._user = undefined;
        this.currentUserInfo.reset();
        this.currentUserSettings.reset();
        resetAllLocalStorageProperties();
        await this._placeMediaDownloader?.reset();
        await this._tileDownloader?.reset();
        localStorage.clear();

        // reload window
        location.reload();
    }

    private _registerAuthEvents() {
        const handler = (_: string, displayText: string, dsEventName: string, toast = false) => {
            this._createAWSHubHandler("auth-changed", displayText, dsEventName, undefined, toast);
        };

        const listener = async (data: any) => {
            switch (data.payload.event) {
                case "signedIn":
                    {
                        await this._setCurrentUserInfo();
                        handler(data.payload.event, "user signed in", "auth-user-signed-in");
                        // reload window
                        location.reload();
                    }
                    break;
                case "signedOut":
                    await this._signOut();
                    break;
                case "signInWithRedirect_failure":
                    handler(
                        data.payload.event,
                        "user sign in failure",
                        "auth-user-sign-in-failure"
                    );
                    break;
                case "tokenRefresh":
                    handler(
                        data.payload.event,
                        "token refresh succeeded",
                        "auth-user-token-refresh-succeeded",
                        false
                    );
                    break;
                case "tokenRefresh_failure":
                    handler(
                        data.payload.event,
                        "token refresh failed",
                        "auth-user-token-refresh-failed",
                        false
                    );
                    await this._signOut();
                    break;
                case "configured": {
                    handler(data.payload.event, "the Auth module is configured", "auth-configured");
                }
            }
        };

        Hub.listen("auth", listener);
    }

    private _registerDataStoreEvents() {
        // https://docs.amplify.aws/lib/datastore/datastore-events/q/platform/js/
        const handler = (data: any, displayText: string, dsEventName: string, toast = false) => {
            getModuleLog("datastore").debug(`${displayText}({0})`, data.payload.data);
            this._createAWSHubHandler("data-store", displayText, dsEventName, data, toast);
        };

        const listener = async (data: any) => {
            switch (data.payload.event) {
                case "networkStatus":
                    {
                        const active = data.payload.data.active;
                        const text = active ? "App online" : "App offline";
                        handler(data, text, "network-status", true);
                        this._log.debug(`Network status - ${active ? "online" : "offline"}`);
                    }
                    break;
                case "subscriptionsEstablished":
                    handler(
                        data,
                        "Subscriptions established",
                        "datastore-subscriptions-established"
                    );
                    break;
                case "syncQueriesStarted":
                    handler(data, "Sync queries started", "datastore-sync-queries-started");
                    break;
                case "modelSynced":
                    handler(data, "Model synced", "datastore-model-synced");
                    break;
                case "syncQueriesReady":
                    handler(data, "Sync queries ready", "datastore-sync-queries-ready");
                    break;
                case "ready":
                    handler(data, "Datastore ready", "datastore-ready");
                    break;
            }
        };

        Hub.listen("datastore", listener);
    }
}
